// Courier engine: little agents that walk between houses carrying items.
// Each courier is a real handoff — WorldMap calls dispatch() when Grok Bot logs
// a handoff event (MCP log_agent_event type "handoff"). A drawn ROUTE between
// the two agents is used when one exists; otherwise the courier walks straight
// from house to house.

import { useEffect, useRef, useState, useCallback } from "react";
import { ROUTES, AGENT_LAYOUT } from "../config/layout";

const MAX_ACTIVE = 6;

function segGeo(pts) {
  const lens = [];
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i][0] - pts[i - 1][0];
    const dy = (pts[i][1] - pts[i - 1][1]) * 0.75; // 4:3 aspect correction
    const l = Math.sqrt(dx * dx + dy * dy);
    lens.push(l);
    total += l;
  }
  return { lens, total };
}

function pointAt(pts, geo, dist) {
  let d = dist;
  for (let i = 0; i < geo.lens.length; i++) {
    if (d <= geo.lens[i] || i === geo.lens.length - 1) {
      const t = geo.lens[i] ? Math.min(1, d / geo.lens[i]) : 1;
      return {
        x: pts[i][0] + (pts[i + 1][0] - pts[i][0]) * t,
        y: pts[i][1] + (pts[i + 1][1] - pts[i][1]) * t,
      };
    }
    d -= geo.lens[i];
  }
  return { x: pts[pts.length - 1][0], y: pts[pts.length - 1][1] };
}

/**
 * @param world      "leads" | "clients" | "internal" | null
 * @param onDeliver  (toAgentId) => void — pulse the receiving house
 */
export function useCouriers(world, onDeliver) {
  const [couriers, setCouriers] = useState([]);
  const stateRef = useRef([]);
  const idRef = useRef(0);
  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // clear on world change
  useEffect(() => {
    stateRef.current = [];
    setCouriers([]);
  }, [world]);

  /** Send a courier for a handoff. Ignored when either house isn't in this world. */
  const dispatch = useCallback(
    ({ from, to, item, message }) => {
      if (!world || reduceMotion || stateRef.current.length >= MAX_ACTIVE) return;
      const drawn = (ROUTES[world] || []).find((r) => r.from === from && r.to === to);
      const layout = AGENT_LAYOUT[world] || {};
      const route = drawn
        ? { ...drawn, item: item || drawn.item, label: message || drawn.label }
        : layout[from] && layout[to]
          ? { from, to, item: item || "📨", label: message || "Handoff", pts: [[layout[from].x, layout[from].y], [layout[to].x, layout[to].y]] }
          : null;
      if (!route) return;
      stateRef.current.push({
        id: idRef.current++,
        route,
        geo: segGeo(route.pts),
        dist: 0,
        speed: 7 + Math.random() * 3, // %/second
      });
    },
    [world, reduceMotion]
  );

  // animation loop
  useEffect(() => {
    if (!world || reduceMotion) return undefined;
    let raf;
    let last = performance.now();
    const frame = (now) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      let changed = false;
      const next = [];
      for (const c of stateRef.current) {
        c.dist += c.speed * dt;
        if (c.dist >= c.geo.total) {
          onDeliver?.(c.route.to);
          changed = true;
        } else {
          next.push(c);
          changed = true;
        }
      }
      stateRef.current = next;
      if (changed) {
        setCouriers(
          next.map((c) => ({
            id: c.id,
            from: c.route.from,
            item: c.route.item,
            label: c.route.label,
            ...pointAt(c.route.pts, c.geo, c.dist),
          }))
        );
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [world, onDeliver, reduceMotion]);

  return { couriers, dispatch };
}
